import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Box } from '@mui/material';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import { usePrimaryColor } from 'styles/theme';
import StyChip from 'routes/HumanResourcesModule/components/ui/StyChip';
import {
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_NAME,
    ORG_CHART_HR_SOFTWARE_ID,
} from 'routes/HumanResourcesModule/business/definitions';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { PAY_COMPONENT } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { SimulationPayComponentsData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/PayComponents/helpers/definitions';
import { StyChipLoading } from 'routes/HumanResourcesModule/components/ui/StyChipLoading';
import { TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import EnvelopeHypothesisModal from '../EnvelopeHypothesisModal';
import { useEnvelopePayComponentHypothesisCRUDMutations } from './helpers/hooks/payComponentHypothesis';
import { MANTY_COLORS, UNMAPPED } from 'business/constants';

type CreateEnvelopePayComponentHypothesisCellRendererParams =
    CellRendererParams<SimulationPayComponentsData> & {
        hrSimulation: HrSimulation;
    };

const CreateEnvelopePayComponentHypothesisCellRenderer = ({
    data,
    hrSimulation,
}: CreateEnvelopePayComponentHypothesisCellRendererParams): React.ReactElement => {
    const { t } = useTranslation();
    const primaryColor: string = usePrimaryColor();
    const [isOpen, setIsOpen] = useState(false);

    const orgChartHrSoftwareId = data?.[ORG_CHART_HR_SOFTWARE_ID];
    const orgChartHrMapping = data?.[ORG_CHART_HR_MAPPING];
    const orgChartHrName = data?.[ORG_CHART_HR_NAME];
    const payComponent = data?.[PAY_COMPONENT];
    const isOrgChartObsolete = orgChartHrSoftwareId === UNMAPPED;

    const { createHypothesis, isLoading } =
        useEnvelopePayComponentHypothesisCRUDMutations({
            simulationId: hrSimulation.id,
            trackEventKey:
                TRACKABLE_EVENTS.human_resources.simulation.payComponents
                    .validateUpdateComponentEnvelopeHypothesis,
            orgChartHrSoftwareId,
            payComponent,
        });

    return (
        <Box>
            {isLoading ? (
                <StyChipLoading />
            ) : (
                <StyChip
                    label={
                        isOrgChartObsolete
                            ? t('humanResources.obsoleteService')
                            : t('humanResources.simulation.addHypothesis')
                    }
                    onClick={() => {
                        if (isOrgChartObsolete) return;
                        setIsOpen(true);
                    }}
                    icon={
                        isOrgChartObsolete ? <></> : <AddCircleOutlinedIcon />
                    }
                    styledColor={
                        isOrgChartObsolete
                            ? MANTY_COLORS.darkGrey
                            : primaryColor
                    }
                />
            )}
            {isOpen &&
                payComponent !== undefined &&
                orgChartHrMapping !== undefined &&
                orgChartHrName !== undefined &&
                orgChartHrSoftwareId !== undefined && (
                    <EnvelopeHypothesisModal
                        open={isOpen}
                        onClose={() => setIsOpen(false)}
                        hrSimulation={hrSimulation}
                        envelopeField={PAY_COMPONENT}
                        envelopeFieldValue={payComponent}
                        orgChartHrMapping={orgChartHrMapping}
                        orgChartHrName={orgChartHrName}
                        orgChartHrSoftwareId={orgChartHrSoftwareId}
                        hypothesisCRUDOperations={{
                            createHypothesis,
                        }}
                    />
                )}
        </Box>
    );
};

export default CreateEnvelopePayComponentHypothesisCellRenderer;
